import React, { Component } from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import DialogFooter from "components/Dialog/DialogFooter.jsx";

import api from "api/index";

const ROLES = ["Principal", "Registrar", "Bursar", "Dean of Studies", "Lecturer", "Librarian", "Administrator", "Super Admin"];

const BLANK_FORM = { name: "", email: "", role: "", password: "" };

const currentUserId = () => {
  try { return JSON.parse(localStorage.getItem("bwillis_user") || "{}").id; } catch (e) { return null; }
};

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
};

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "220px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #0d1b6e 0%, #1a237e 60%, #1565c0 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    color: "#fff"
  },
  heroTitle: { fontSize: "32px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },

  kpiRow: { display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: "200px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    border: "1px solid #f1f5f9"
  },
  iconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "20px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "12px", color: "#64748b", margin: "2px 0 0", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" },

  toolbarWrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 25px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    gap: "20px",
    flexWrap: "wrap",
    borderRadius: "16px 16px 0 0"
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    background: "#fff",
    padding: "8px 16px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    width: "350px"
  },
  searchInput: {
    border: "none",
    outline: "none",
    marginLeft: "10px",
    fontSize: "14px",
    width: "100%",
    color: "#1e293b"
  },

  errorMsg: { color: "#f44336", fontSize: "13px" },
  formControl: { width: "100%", marginTop: "16px" },
  passwordRow: { display: "flex", gap: "10px", alignItems: "flex-end" },
  selfBadge: { fontSize: "11px", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }
};

class UserList extends Component {
  state = {
    users: [],
    loading: true,
    open: false,
    editing: null,
    form: { ...BLANK_FORM },
    saving: false,
    error: null,
    formError: null,
    confirmToggle: null,
    resetTarget: null,
    resetPassword: "",
    resetting: false,
    resetError: null,
    resetDone: false,
    searchQuery: ""
  };

  componentDidMount() {
    this.fetchUsers();
  }

  fetchUsers = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await api.get("/auth/users");
      const d = res.data.data || res.data;
      this.setState({ users: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) {
      this.setState({ users: [], loading: false, error: "Could not load users." });
    }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  openAdd = () => {
    this.setState({
      open: true,
      editing: null,
      formError: null,
      form: { ...BLANK_FORM, password: genPassword() }
    });
  };

  openEdit = user => {
    this.setState({
      open: true,
      editing: user.id,
      formError: null,
      form: { name: user.name || "", email: user.email || "", role: user.role || "", password: "" }
    });
  };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.name.trim() || !form.email.trim() || !form.role) {
      this.setState({ formError: "Name, email, and role are required." });
      return;
    }
    if (!editing && !form.password.trim()) {
      this.setState({ formError: "A temporary password is required for new users." });
      return;
    }
    this.setState({ saving: true, formError: null });
    try {
      if (editing) {
        await api.put(`/auth/users/${editing}`, { name: form.name.trim(), email: form.email.trim(), role: form.role });
      } else {
        await api.post("/auth/users", {
          name: form.name.trim(), email: form.email.trim(), role: form.role, password: form.password.trim()
        });
      }
      this.setState({ open: false, saving: false });
      this.fetchUsers();
    } catch (e) {
      this.setState({ formError: e.response?.data?.message || `Failed to ${editing ? "update" : "add"} user.`, saving: false });
    }
  };

  openResetPassword = user => {
    this.setState({
      resetTarget: user,
      resetPassword: genPassword(),
      resetError: null,
      resetDone: false
    });
  };

  handleResetPassword = async () => {
    const { resetTarget, resetPassword } = this.state;
    if (!resetTarget) return;
    this.setState({ resetting: true, resetError: null });
    try {
      await api.post(`/auth/users/${resetTarget.id}/reset-password`, { password: resetPassword });
      this.setState({ resetting: false, resetDone: true });
    } catch (e) {
      this.setState({ resetting: false, resetError: e.response?.data?.message || "Failed to reset password." });
    }
  };

  handleToggleStatus = async () => {
    const { confirmToggle } = this.state;
    if (!confirmToggle) return;
    try {
      await api.patch(`/auth/users/${confirmToggle.id}`, {
        status: confirmToggle.status === "active" ? "inactive" : "active"
      });
      this.setState({ confirmToggle: null });
      this.fetchUsers();
    } catch (e) {
      this.setState({ confirmToggle: null, error: e.response?.data?.message || "Failed to update user status." });
    }
  };

  render() {
    const { classes } = this.props;
    const {
      users, loading, searchQuery,
      open, editing, form, saving, error, formError, confirmToggle,
      resetTarget, resetPassword, resetting, resetError, resetDone
    } = this.state;

    const myId = currentUserId();
    const q = searchQuery.toLowerCase();
    const filteredUsers = users.filter(u =>
      (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
    );

    const totalUsers = users.length;
    const activeUsers = users.filter(u => u.status === "active").length;
    const inactiveUsers = users.filter(u => u.status !== "active").length;
    const adminUsers = users.filter(u => u.role === "Administrator" || u.role === "Principal").length;

    const columns = [
      { id: "name", header: "Name", accessorFn: u => u.name || "—" },
      { id: "email", header: "Email", accessorFn: u => u.email || "—" },
      { id: "role", header: "Role", accessorFn: u => u.role || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const u = info.row.original;
          return <Pill tone={u.status === "active" ? "success" : "muted"}>{u.status || "active"}</Pill>;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => {
          const u = info.row.original;
          const isSelf = u.id === myId;
          return (
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", alignItems: "center" }}>
              <Button size="sm" color="info" simple onClick={() => this.openEdit(u)} style={{ padding: "6px" }}>
                <Icon style={{ fontSize: "20px" }}>edit</Icon>
              </Button>
              <Button size="sm" color="warning" simple onClick={() => this.openResetPassword(u)} style={{ padding: "6px" }}>
                <Icon style={{ fontSize: "20px" }}>vpn_key</Icon>
              </Button>
              {isSelf ? (
                <span className={classes.selfBadge}>You</span>
              ) : (
                <Button
                  size="sm"
                  color={u.status === "active" ? "danger" : "success"}
                  simple
                  onClick={() => this.setState({ confirmToggle: { id: u.id, name: u.name, status: u.status } })}
                  style={{ padding: "6px" }}
                >
                  <Icon style={{ fontSize: "20px" }}>{u.status === "active" ? "block" : "check_circle"}</Icon>
                </Button>
              )}
            </div>
          );
        }
      }
    ];

    const emptyState = (
      <div style={{ textAlign: "center", padding: "80px 40px" }}>
        <Icon style={{ fontSize: "64px", color: "#e2e8f0", marginBottom: "16px" }}>manage_accounts</Icon>
        <p style={{ color: "#64748b", fontSize: "16px", margin: 0 }}>
          {searchQuery ? "No users match your search." : "No users found. Click 'New User' to start."}
        </p>
      </div>
    );

    return (
      <GridContainer>
        <GridItem xs={12}>
          <div className={classes.heroWrap}>
            <div className={classes.heroOverlay} />
            <div className={classes.heroContent}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                <div>
                  <h1 className={classes.heroTitle}>System Users</h1>
                  <p className={classes.heroSub}>
                    Manage administrator, staff, and system login accounts.
                  </p>
                </div>
                <Button
                  color="white"
                  round
                  onClick={this.openAdd}
                  style={{ fontWeight: "800", padding: "12px 24px", boxShadow: "0 4px 14px 0 rgba(0,0,0,0.2)" }}
                >
                  <Icon style={{ marginRight: "8px" }}>person_add</Icon> New User
                </Button>
              </div>
            </div>
          </div>
        </GridItem>

        <GridItem xs={12}>
          <div className={classes.kpiRow}>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e3f2fd", color: "#1565c0" }}>
                <Icon>people</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{totalUsers}</p>
                <p className={classes.statLabel}>Total Users</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e8f5e9", color: "#2e7d32" }}>
                <Icon>check_circle</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{activeUsers}</p>
                <p className={classes.statLabel}>Active</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#ffebee", color: "#c62828" }}>
                <Icon>block</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{inactiveUsers}</p>
                <p className={classes.statLabel}>Inactive</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e8eaf6", color: "#3949ab" }}>
                <Icon>admin_panel_settings</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{adminUsers}</p>
                <p className={classes.statLabel}>Administrators</p>
              </div>
            </div>
          </div>
        </GridItem>

        <GridItem xs={12}>
          <div className={classes.toolbarWrap}>
            <div className={classes.searchBox}>
              <Icon style={{ color: "#94a3b8", fontSize: "20px" }}>search</Icon>
              <input
                type="text"
                className={classes.searchInput}
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={e => this.setState({ searchQuery: e.target.value })}
              />
            </div>
            <div style={{ color: "#64748b", fontSize: "14px", fontWeight: 500 }}>
              Showing <b>{filteredUsers.length}</b> users
            </div>
          </div>
          {error && <p className={classes.errorMsg} style={{ padding: "10px 0" }}>{error}</p>}
          <DataTable
            columns={columns}
            data={filteredUsers}
            isLoading={loading}
            emptyState={emptyState}
          />
        </GridItem>

        {/* Add/Edit Dialog */}
        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="xs" fullWidth>
          <DialogHeader
            title={editing ? "Edit User" : "New User"}
            subtitle={editing ? "Update this user's details." : "Create a new system login account."}
            icon={editing ? "edit" : "person_add"}
            color={editing ? "primary" : "info"}
            onClose={() => this.setState({ open: false })}
          />
          <DialogContent style={{ paddingTop: "20px" }}>
            {formError && <p className={classes.errorMsg} style={{ marginBottom: "15px" }}>{formError}</p>}
            <CustomInput
              labelText="Full Name"
              id="userEditName"
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: form.name,
                onChange: this.handleChange("name"),
                placeholder: "e.g. Jane Namuli"
              }}
            />
            <CustomInput
              labelText="Email Address"
              id="userEditEmail"
              formControlProps={{ fullWidth: true }}
              inputProps={{
                value: form.email,
                onChange: this.handleChange("email"),
                type: "email",
                placeholder: "jane.namuli@bwptc.ac.ug"
              }}
            />
            <FormControl className={classes.formControl}>
              <InputLabel>Role</InputLabel>
              <Select value={form.role} onChange={this.handleChange("role")}>
                <MenuItem value="">— Select —</MenuItem>
                {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            {!editing && (
              <div className={classes.passwordRow}>
                <CustomInput
                  labelText="Temporary Password"
                  id="userEditPassword"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    value: form.password,
                    onChange: this.handleChange("password")
                  }}
                  helpText="Sent to the user by email. They can change it after logging in."
                />
                <Button size="sm" simple color="info" onClick={() => this.setState(prev => ({ form: { ...prev.form, password: genPassword() } }))}>
                  Regenerate
                </Button>
              </div>
            )}
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving} style={{ fontWeight: "700" }}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : (editing ? "Update Details" : "Create User")}
            </Button>
          </DialogFooter>
        </Dialog>

        {/* Activate/Deactivate Confirmation Dialog */}
        <Dialog open={!!confirmToggle} onClose={() => this.setState({ confirmToggle: null })} maxWidth="xs" fullWidth>
          <DialogHeader
            title={confirmToggle && confirmToggle.status === "active" ? "Deactivate User" : "Activate User"}
            icon={confirmToggle && confirmToggle.status === "active" ? "block" : "check_circle"}
            color={confirmToggle && confirmToggle.status === "active" ? "danger" : "success"}
            onClose={() => this.setState({ confirmToggle: null })}
          />
          <DialogContent style={{ paddingTop: "20px", textAlign: "center" }}>
            <div style={{
              width: "60px", height: "60px", borderRadius: "50%",
              background: confirmToggle && confirmToggle.status === "active" ? "#ffebee" : "#e8f5e9",
              display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px"
            }}>
              <Icon style={{
                color: confirmToggle && confirmToggle.status === "active" ? "#d32f2f" : "#2e7d32",
                fontSize: "32px"
              }}>
                {confirmToggle && confirmToggle.status === "active" ? "block" : "check_circle"}
              </Icon>
            </div>
            <h5 style={{ fontWeight: "700", marginBottom: "10px" }}>Are you sure?</h5>
            <p style={{ color: "#666" }}>
              {confirmToggle && confirmToggle.status === "active"
                ? `${confirmToggle.name} will no longer be able to log in until reactivated.`
                : `${confirmToggle ? confirmToggle.name : "This user"} will regain access to log in.`}
            </p>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ confirmToggle: null })}>Cancel</Button>
            <Button
              color={confirmToggle && confirmToggle.status === "active" ? "danger" : "success"}
              onClick={this.handleToggleStatus}
              style={{ fontWeight: "700" }}
            >
              {confirmToggle && confirmToggle.status === "active" ? "Deactivate" : "Activate"}
            </Button>
          </DialogFooter>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={!!resetTarget} onClose={() => this.setState({ resetTarget: null })} maxWidth="xs" fullWidth>
          <DialogHeader
            title="Reset Password"
            subtitle={resetTarget ? `Set a new temporary password for ${resetTarget.name}.` : ""}
            icon="vpn_key"
            color="warning"
            onClose={() => this.setState({ resetTarget: null })}
          />
          <DialogContent style={{ paddingTop: "20px" }}>
            {resetError && <p className={classes.errorMsg} style={{ marginBottom: "15px" }}>{resetError}</p>}
            {resetDone ? (
              <p style={{ color: "#2e7d32", fontWeight: 600 }}>
                Password reset. The new password has been emailed to {resetTarget && resetTarget.email ? resetTarget.email : "the user"}.
              </p>
            ) : (
              <div className={classes.passwordRow}>
                <CustomInput
                  labelText="New Temporary Password"
                  id="resetPasswordField"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    value: resetPassword,
                    onChange: e => this.setState({ resetPassword: e.target.value })
                  }}
                  helpText="Emailed to the user. They'll be asked to change it on next login."
                />
                <Button size="sm" simple color="info" onClick={() => this.setState({ resetPassword: genPassword() })}>
                  Regenerate
                </Button>
              </div>
            )}
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ resetTarget: null })} disabled={resetting}>
              {resetDone ? "Close" : "Cancel"}
            </Button>
            {!resetDone && (
              <Button color="primary" onClick={this.handleResetPassword} disabled={resetting || !resetPassword.trim()} style={{ fontWeight: "700" }}>
                {resetting ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Reset Password"}
              </Button>
            )}
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

UserList.propTypes = { classes: PropTypes.object.isRequired };
export default withStyles(styles)(UserList);
